import { StyleSheet } from 'aphrodite';
import {
	antiFlashWhite,
	brandPrimary,
	darkGrayFontColor,
	destructive,
	inputBorderColor,
	titles,
} from '../../styles/colors';
import { shadowSm } from '../../styles/shadows';
import { THEME } from '../Theme/theme';

export const s = StyleSheet.create({
	input: {
		boxSizing: 'border-box',
		border: `1px solid ${inputBorderColor}`,
		display: 'flex',
		height: 36,
		width: '100%',
		minWidth: 0,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.light,
		padding: `4px 12px`,
		fontSize: '1rem',
		lineHeight: '1.5',
		boxShadow: shadowSm,
		transition: 'color, box-shadow',
		outline: 'none',
		color: titles,
		':focus-visible': {
			borderColor: brandPrimary,
		},
		':disabled': {
			pointerEvents: 'none',
			cursor: 'not-allowed',
			opacity: 0.5,
		},
		':placeholder': {
			color: darkGrayFontColor,
		},
		'::file-selector-button': {
			border: 'none',
			padding: 0,
			color: titles,
			display: 'inline-flex',
			height: '100%',
			borderRight: `1px solid ${inputBorderColor}`,
			background: 'transparent',
			fontSize: '0.875rem',
			fontStyle: 'normal',
			lineHeight: '1.43',
			fontWeight: 'bold',
			paddingRight: 12,
			marginRight: 12,
		},
		':is([type="file"])': {
			paddingTop: 0,
			paddingBottom: 0,
		},
		':is([aria-invalid="true"])': {
			borderColor: destructive,
			boxShadow: `#D0021B32 0px 0px 0px 3px`,
		},
		[`@media (min-width: ${THEME.breakpoints.md}px)`]: {
			fontSize: '0.875rem',
			lineHeight: '1.43',
		},
	},
	inputGroup: {
		display: 'flex',
		alignItems: 'stretch',
		':has(*) [data-slot=input]': {
			flexGrow: 1,
		},
		':has(*) [data-slot="input-addon"]:has(+ [data-slot="input"])': {
			borderTopRightRadius: 0,
			borderBottomRightRadius: 0,
			borderRight: 'none',
		},
		':has(*) [data-slot="input"]+[data-slot="input-addon"]': {
			borderTopLeftRadius: 0,
			borderBottomLeftRadius: 0,
			borderLeft: 'none',
		},
		':has(*) [data-slot="input-addon"]:has(+ [data-slot="button"])(': {
			borderTopRightRadius: 0,
			borderBottomRightRadius: 0,
		},
		':has(*) [data-slot="input"]+[data-slot="button"]': {
			borderTopLeftRadius: 0,
			borderBottomLeftRadius: 0,
		},
		':has(*) [data-slot="button"]+[data-slot="input"]': {
			borderTopLeftRadius: 0,
			borderBottomLeftRadius: 0,
		},
		':has(*) [data-slot="input-addon"]+[data-slot="input"]': {
			borderTopLeftRadius: 0,
			borderBottomLeftRadius: 0,
		},
		':has(*) [data-slot="input"]:has(+[data-slot="button"])': {
			borderTopRightRadius: 0,
			borderBottomRightRadius: 0,
		},
		':has(*) [data-slot="input"]:has(+[data-slot="input-addon"])': {
			borderTopRightRadius: 0,
			borderBottomRightRadius: 0,
		},
	},
	inputWrapper: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		':has(:focus-visible)': {
			borderColor: brandPrimary,
		},
		':has(*) [data-slot="input"]': {
			display: 'flex',
			width: '100%',
			outline: 'none',
			transition: 'color',
			color: titles,
			border: 'none',
			background: 'transparent',
			padding: 0,
			boxShadow: 'none',
			height: 'auto',
			borderRadius: 0,
		},
		':has(*) [data-slot="autocomplete-input"]': {
			display: 'flex',
			width: '100%',
			outline: 'none',
			transition: 'color',
			color: titles,
			border: 'none',
			background: 'transparent',
			padding: 0,
			boxShadow: 'none',
			height: 'auto',
			borderRadius: 0,
		},
		':has(*) [data-slot="input"]:focus-visible': {
			boxShadow: 'none',
		},
		':has(*) [data-slot="autocomplete-input"]:focus-visible': {
			boxShadow: 'none',
		},
		':has(*) [data-slot="input"][disabled]': {
			cursor: 'not-allowed',
			opacity: 0.5,
		},
		':has(*) [data-slot="autocomplete-input"][disabled]': {
			cursor: 'not-allowed',
			opacity: 0.5,
		},
		':has(*) svg': {
			color: `${titles}96`,
			flexShrink: 0,
		},
		':has(*) [data-slot="input"]:focus-visible+svg': {
			color: brandPrimary,
		},
		':has(*) [data-slot="autocomplete-input"]:focus-visible+svg': {
			color: brandPrimary,
		},
		':has(*) svg:has(+ [data-slot="input"]:focus-visible)': {
			color: brandPrimary,
		},
		':has(*) svg:has(+ [data-slot="autocomplete-input"]:focus-visible)': {
			color: brandPrimary,
		},
	},
	inputAddon: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		justifyContent: 'center',
		background: antiFlashWhite,
		border: `1px solid ${inputBorderColor}`,
		boxShadow: shadowSm,
		borderRadius: THEME.common.borderRadius,
		height: 36,
		minWidth: 36,
		boxSizing: 'border-box',
		paddingLeft: 12,
		paddingRight: 12,
		fontSize: '0.8125rem',
		lineHeight: '1.43',
		color: titles,
		':has(*) svg': {
			color: `${titles}96`,
		},
		':has([data-slot="select-trigger"])': {
			padding: 0,
		},
		':has(*) [data-slot="select-trigger"]': {
			border: 'none',
			borderRadius: 0,
		},
		':has([data-slot="dropdown-menu-select-trigger"])': {
			padding: 0,
		},
		':has(*) [data-slot="dropdown-menu-select-trigger"]': {
			border: 'none',
			borderRadius: 0,
		},
	},
	inputAddondefault: {},
	inputAddonicon: {
		paddingLeft: 0,
		paddingRight: 0,
		justifyContent: 'center',
	},
});
